import { FC, useMemo, useState } from 'react'
import { User } from '../../user/type'
import { Modal, Space, Switch, Table, Typography } from 'antd'
import { Link } from 'react-router-dom'
import { GetUsers } from '../../user/graphql/user-queries.graphql'
import { useQuery } from '@apollo/client'
import { translate } from '../../../core'
import { UserOutlined } from '@ant-design/icons'
import { ShowIf } from '../../../core/component/ShowIf'
import { dateDisplay, dateFormatSlashed } from '../../../core/until'
import { UserRole } from '../../user/type/user-query'

export type ContactOnlineUserListProps = {
  open: boolean
  onClose: () => void
}
export const ContactOnlineUserList: FC<ContactOnlineUserListProps> = ({ open, onClose }) => {
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(10)
  const [showContactsOnly, setShowContactsOnly] = useState<boolean>(false)

  const { data, loading } = useQuery(GetUsers, {
    variables: {
      request: {
        take: pageSize,
        skip: (currentPage - 1) * pageSize,
        hasContact: showContactsOnly,
        role: {
          eq: UserRole.Customer,
        },
      }
    }
  })
  const users = useMemo(
    () => data?.usersFind.data.map(it => ({ key: it.id?.toString() ?? '', ...it })) ?? [],
    [data]
  )

  const columns = useMemo(() => ([
    {
      dataIndex: 'createdAt',
      title: translate('created_date'),
      width: 140,
      render: (value?: string | Date) => value ? dateDisplay(new Date(value), dateFormatSlashed) : '-',
    },
    {
      dataIndex: 'firstName',
      title: translate('first_name'),
      width: 160,
      ellipsis: true,
    },
    {
      dataIndex: 'lastName',
      title: translate('last_name'),
      width: 160,
      ellipsis: true,
    },
    {
      dataIndex: 'email',
      title: translate('email'),
      width: 260,
      ellipsis: true,
    },
    {
      dataIndex: 'relatedContact',
      title: translate('contact_id'),
      width: 180,
      render: (value: User['relatedContact']) => (
        <ShowIf
          condition={!!value}
          replacement={<Typography.Text type='secondary'>-</Typography.Text>}>
          <Link to={`/contact/${value?.contactId}`}>
            {value?.contactId}
          </Link>
        </ShowIf>
      )
    }
  ]), [])

  return (
    <Modal
      title={<Space>
        <UserOutlined />
        {translate('online', 'users')}
      </Space>}
      onCancel={onClose}
      closable
      footer={null}
      open={open}
      width={1000}
      style={{ maxWidth: '95vw' }}
      bodyStyle={{ maxHeight: '70vh', overflow: 'hidden' }}
      destroyOnClose
    >
      <Space className='w-100 d-flex justify-content-between mb-2'>
        <Space>
        <Switch
          checked={showContactsOnly}
          onChange={checked => {
            setShowContactsOnly(checked)
            setCurrentPage(1)
          }}
        />
        {translate('show', 'contacts', 'only')}
        </Space>
        <Typography.Text type='secondary'>
          {users.length} / {data?.usersFind.count ?? 0}
        </Typography.Text>
      </Space>

      <Table<User>
        className='mt-1'
        size='small'
        tableLayout='fixed'
        loading={loading}
        scroll={{ x: 900, y: 'calc(70vh - 240px)' }}
        columns={columns}
        dataSource={users}
        pagination={{
          current: currentPage,
          total: data?.usersFind.count,
          position: ['bottomCenter'],
          pageSize: pageSize,
          showSizeChanger: true,
        }}
        onChange={e => {
          if (e.current) {
            setCurrentPage(e.current)
          }

          if (e.pageSize && e.pageSize !== pageSize) {
            setPageSize(e.pageSize)
            setCurrentPage(1)
          }
        }}
      />
    </Modal>
  )
}